import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { t } from "../../locales";
import { GET, PUT } from "../../services/axios.service";
import { useToastHandler } from "../../hooks/useToastHandler";
import { useAppSelector } from "../../app/hooks";
import { getSelectedBranch } from "../../features/settings/settingsSlice";

type FormData = {
  company_name: string;
  company_name_report: string;
  company_tagline_report: string;
  company_address_1: string;
  company_address_2: string;
  company_owner_1: string;
  company_owner_phone_1: string;
  company_owner_2: string;
  company_owner_phone_2: string;
};

const EMPTY_FORM: FormData = {
  company_name: "",
  company_name_report: "",
  company_tagline_report: "",
  company_address_1: "",
  company_address_2: "",
  company_owner_1: "",
  company_owner_phone_1: "",
  company_owner_2: "",
  company_owner_phone_2: "",
};

const ReportSettingsForm = () => {
  const { handleSuccess, handleError } = useToastHandler();
  const selectedBranch = useAppSelector(getSelectedBranch);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const { handleSubmit, register, reset } = useForm<FormData>({
    defaultValues: EMPTY_FORM,
  });

  useEffect(() => {
    if (!selectedBranch?.id) return;
    let cancelled = false;
    (async () => {
      setIsLoading(true);
      try {
        const response: any = await GET("/v1/settings", {});
        if (cancelled) return;
        if (response?.data?.status === "success") {
          const data = response.data.data || {};
          reset({
            company_name: data.company_name ?? "",
            company_name_report: data.company_name_report ?? "",
            company_tagline_report: data.company_tagline_report ?? "",
            company_address_1: data.company_address_1 ?? "",
            company_address_2: data.company_address_2 ?? "",
            company_owner_1: data.company_owner_1 ?? "",
            company_owner_phone_1: data.company_owner_phone_1 ?? "",
            company_owner_2: data.company_owner_2 ?? "",
            company_owner_phone_2: data.company_owner_phone_2 ?? "",
          });
        } else {
          handleError(response);
        }
      } catch (error: any) {
        if (!cancelled) handleError(error);
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [selectedBranch?.id, reset]);

  const onSubmit = async (data: FormData) => {
    setIsSaving(true);
    try {
      const response: any = await PUT("/v1/settings", data);
      if (response?.data?.status === "success") {
        handleSuccess(response);
      } else {
        handleError(response);
      }
    } catch (error: any) {
      handleError(error);
    }
    setIsSaving(false);
  };

  const inputBase =
    "w-full h-12 px-4 text-sm text-brand-dark bg-white border-2 rounded-xl outline-none box-border transition-[border-color,box-shadow] duration-200 placeholder:text-gray-400 focus:border-brand-dark focus:shadow-focus-brand border-gray-200 disabled:bg-gray-100 disabled:text-gray-500 disabled:cursor-not-allowed";

  const renderField = (
    name: keyof FormData,
    label: string,
    placeholder: string
  ) => (
    <div className="flex flex-col">
      <label className="text-sm font-semibold text-brand-dark mb-[7px]">{label}</label>
      <input
        className={inputBase}
        type="text"
        placeholder={placeholder}
        disabled={isLoading || isSaving}
        {...register(name)}
      />
    </div>
  );

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-6">
      {/* Company Info */}
      <div className="bg-white rounded-2xl shadow-card overflow-hidden">
        <div className="bg-gray-50 px-8 py-5 border-b border-gray-100">
          <h3 className="text-xl font-bold text-brand-dark">{t.settings.companyInfoTitle}</h3>
          <p className="text-sm text-gray-500 mt-1">{t.settings.companyInfoSubtitle}</p>
        </div>
        <div className="p-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {renderField("company_name", t.settings.companyNameLabel, t.settings.companyNamePlaceholder)}
            {renderField("company_name_report", t.settings.companyNameReportLabel, t.settings.companyNameReportPlaceholder)}
            {renderField("company_tagline_report", t.settings.companyTaglineReportLabel, t.settings.companyTaglineReportPlaceholder)}
          </div>
        </div>
      </div>

      {/* Address */}
      <div className="bg-white rounded-2xl shadow-card overflow-hidden">
        <div className="bg-gray-50 px-8 py-5 border-b border-gray-100">
          <h3 className="text-xl font-bold text-brand-dark">{t.settings.addressTitle}</h3>
          <p className="text-sm text-gray-500 mt-1">{t.settings.addressSubtitle}</p>
        </div>
        <div className="p-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {renderField("company_address_1", t.settings.companyAddress1Label, t.settings.companyAddress1Placeholder)}
            {renderField("company_address_2", t.settings.companyAddress2Label, t.settings.companyAddress2Placeholder)}
          </div>
        </div>
      </div>

      {/* Contacts */}
      <div className="bg-white rounded-2xl shadow-card overflow-hidden">
        <div className="bg-gray-50 px-8 py-5 border-b border-gray-100">
          <h3 className="text-xl font-bold text-brand-dark">{t.settings.contactsTitle}</h3>
          <p className="text-sm text-gray-500 mt-1">{t.settings.contactsSubtitle}</p>
        </div>
        <div className="p-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {renderField("company_owner_1", t.settings.companyOwner1Label, t.settings.companyOwner1Placeholder)}
            {renderField("company_owner_phone_1", t.settings.companyOwnerPhone1Label, t.settings.companyOwnerPhone1Placeholder)}
            {renderField("company_owner_2", t.settings.companyOwner2Label, t.settings.companyOwner2Placeholder)}
            {renderField("company_owner_phone_2", t.settings.companyOwnerPhone2Label, t.settings.companyOwnerPhone2Placeholder)}
          </div>
        </div>
      </div>

      <div className="flex justify-end">
        <button
          className="px-8 py-3 bg-[#113F67] text-white font-semibold rounded-lg transition-colors duration-200 hover:bg-[#0d2f4d] disabled:opacity-60 disabled:cursor-not-allowed"
          type="submit"
          disabled={isLoading || isSaving}
        >
          {isSaving ? (
            <div className="flex items-center gap-2">
              <span className="w-4 h-4 rounded-full border-2 border-white/30 border-t-white animate-spin shrink-0"></span>
              {t.settings.saving}
            </div>
          ) : (
            t.settings.saveButton
          )}
        </button>
      </div>
    </form>
  );
};

export default ReportSettingsForm;
